/**
 * Onboarding / Setup Guide v3 client (ADR-019 §D8 / S4.B.1).
 *
 * Backend driven catalog + per-tenant state. The shared
 * <SetupGuide section="..." /> component (S4.B.3) consumes
 * everything from this module — page-level code never touches
 * these endpoints directly.
 */
import { http } from './client';
import type { Schema } from './types';

export type OnboardingStep = Schema<'OnboardingStepResponse'>;
export type OnboardingActionResult = Schema<'OnboardingActionResponse'>;
export type OnboardingResetResult = Schema<'OnboardingResetResponse'>;

/** Sections that have a dedicated SetupGuide instance. The catalog
 *  may grow new sections later; the component falls back to "no
 *  steps" gracefully if the requested section has nothing seeded. */
export type OnboardingSection =
  | 'attendance'
  | 'organization'
  | 'system'
  | 'integrations'
  | 'processes'
  | 'dashboard';

export const onboardingApi = {
  /** Fetch the steps applicable to the current org's plan +
   *  licensed_products + caller's roles. Server-side computes
   *  ``done`` per the precedence ladder (manual → dismiss → auto →
   *  pending). */
  list: (params: { section?: OnboardingSection; lang?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.section) qs.set('section', params.section);
    if (params.lang) qs.set('lang', params.lang);
    const suffix = qs.toString() ? `?${qs}` : '';
    return http.get<OnboardingStep[]>(`/v1/onboarding/steps${suffix}`);
  },

  /** Hide a step. Backend persists across devices. */
  dismiss: (stepId: string) =>
    http.post<OnboardingActionResult>(
      `/v1/onboarding/steps/${stepId}/dismiss`,
    ),

  /** Mark a step done manually (admin override). Wins over both
   *  dismiss and auto-detect: the badge becomes 'manual'. */
  complete: (stepId: string) =>
    http.post<OnboardingActionResult>(
      `/v1/onboarding/steps/${stepId}/complete`,
    ),

  /** Reset all dismissed steps so they reappear (manual completions
   *  stay). Used by the TopNav "Setup Guide" menu action. */
  reset: () => http.post<OnboardingResetResult>('/v1/onboarding/steps/reset'),
};
